import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext } from 'react';

import type { CurrentAmplifyUser } from '../api/ampUser';
import { getCurrentAmplifyUser } from '../api/ampUser';

interface AmplifyUserContextValue {
  user: CurrentAmplifyUser | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  // Methods to interact with the cache:
  refetchUser: () => void;
  clearUserCache: () => void;
}

export const AmplifyUserContext = createContext<
  AmplifyUserContextValue | undefined
>(undefined);

AmplifyUserContext.displayName = 'AmplifyUserContext';

const USER_QUERY_KEY = ['amplifyUser'];

export function AmplifyUserProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<CurrentAmplifyUser>({
    queryKey: USER_QUERY_KEY,
    queryFn: getCurrentAmplifyUser,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const refetchUser = () => {
    refetch();
  };

  const clearUserCache = () => {
    queryClient.removeQueries({ queryKey: USER_QUERY_KEY });
  };

  return (
    <AmplifyUserContext.Provider
      value={{ user, isLoading, isError, error, refetchUser, clearUserCache }}
    >
      {children}
    </AmplifyUserContext.Provider>
  );
}

AmplifyUserProvider.displayName = 'AmplifyUserProvider';
